import { Observable, OperatorFunction, from, of } from 'rxjs';
import { map, mergeMap, toArray } from 'rxjs/operators';

export interface JoinObservableWithArrayOptions<InputKey, OutputKey> {
    inputKey?: InputKey;
    outputKey?: OutputKey;
}

/**
 * Joins an observable with the result of a function that returns a large array.
 * Useful for combining arrays with shared related keys.
 *
 * @param outputArrayMappingFunc - The output array values function
 * @param joinFunc - The joining func
 * @param options - The options for modifying the output keys
 * @returns - The observable result
 */
export function joinObservableWithArray<
    InputType,
    OutputType,
    InputKey extends string = 'input',
    OutputKey extends string = 'output',
    R extends {
        [key in InputKey]: InputType;
    } & {
        [key in OutputKey]?: OutputType;
    } = {
        [key in InputKey]: InputType;
    } & {
        [key in OutputKey]?: OutputType;
    },
>(
    outputArrayMappingFunc: (value: InputType[], index: number) => Observable<OutputType[]> | Promise<OutputType[]>,
    joinFunc: (input: InputType, output: OutputType) => boolean,
    options?: JoinObservableWithArrayOptions<InputKey, OutputKey>,
): OperatorFunction<InputType, R> {
    return source$ =>
        source$.pipe(
            toArray(),
            mergeMap(input =>
                of(input).pipe(
                    mergeMap(outputArrayMappingFunc),
                    map(output => ({
                        input,
                        output,
                    })),
                ),
            ),
            mergeMap(({ input: inputArray, output }) =>
                from(inputArray).pipe(
                    map(
                        input =>
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                            ({
                                [options?.inputKey ?? 'input']: input,
                                [options?.outputKey ?? 'output']: output.find(out => joinFunc(input, out)),
                            }) as R,
                    ),
                ),
            ),
        );
}
